/**
 * Composer contenteditable: su iPhone <input type="text"> blocca la tastiera GIF/sticker
 * ("Le GIF non sono disponibili in questa app"). Contenteditable + paste immagini lo evita.
 */

export function getComposerText(el: HTMLElement): string {
  return (el.innerText || el.textContent || '').replace(/\u00a0/g, ' ').trimEnd()
}

export function setComposerText(el: HTMLElement, text: string): void {
  el.textContent = text
  el.classList.toggle('is-empty', !text.trim())
}

export function clearComposer(el: HTMLElement): void {
  el.innerHTML = ''
  el.classList.add('is-empty')
}

export function wireComposerEditable(
  el: HTMLElement,
  opts: {
    onSubmit: () => void
    onInput?: () => void
    onImageFiles?: (files: File[]) => void
  },
): () => void {
  el.setAttribute('contenteditable', 'true')
  el.setAttribute('role', 'textbox')
  el.setAttribute('aria-multiline', 'false')
  el.setAttribute('enterkeyhint', 'send')
  el.setAttribute('inputmode', 'text')
  el.spellcheck = true
  if (!getComposerText(el)) el.classList.add('is-empty')

  const harvestInlineImages = () => {
    const imgs = Array.from(el.querySelectorAll('img'))
    if (!imgs.length || !opts.onImageFiles) return
    const jobs = imgs.map(async (img, i) => {
      const src = img.getAttribute('src') || ''
      img.remove()
      if (!src.startsWith('blob:') && !src.startsWith('data:')) return null
      try {
        const blob = await (await fetch(src)).blob()
        const ext = blob.type.includes('gif')
          ? 'gif'
          : blob.type.includes('webp')
            ? 'webp'
            : blob.type.includes('png')
              ? 'png'
              : 'jpg'
        return new File([blob], `sticker-${i}.${ext}`, { type: blob.type || 'image/png' })
      } catch {
        return null
      }
    })
    void Promise.all(jobs).then((list) => {
      const files = list.filter((f): f is File => f != null)
      if (files.length) opts.onImageFiles?.(files)
    })
  }

  const onInput = () => {
    harvestInlineImages()
    el.classList.toggle('is-empty', !getComposerText(el).trim())
    opts.onInput?.()
  }

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      opts.onSubmit()
    }
  }

  const onPaste = (e: ClipboardEvent) => {
    const items = e.clipboardData?.items
    if (!items?.length) return
    const files: File[] = []
    for (const item of Array.from(items)) {
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        const f = item.getAsFile()
        if (f) files.push(f)
      }
    }
    if (files.length && opts.onImageFiles) {
      e.preventDefault()
      opts.onImageFiles(files)
      return
    }
    // Solo testo plain — evita HTML sporco da iOS
    const text = e.clipboardData?.getData('text/plain')
    if (text != null) {
      e.preventDefault()
      document.execCommand('insertText', false, text)
    }
  }

  const onFocus = () => el.classList.add('is-focused')
  const onBlur = () => el.classList.remove('is-focused')

  el.addEventListener('input', onInput)
  el.addEventListener('keydown', onKeyDown)
  el.addEventListener('paste', onPaste)
  el.addEventListener('focus', onFocus)
  el.addEventListener('blur', onBlur)

  return () => {
    el.removeEventListener('input', onInput)
    el.removeEventListener('keydown', onKeyDown)
    el.removeEventListener('paste', onPaste)
    el.removeEventListener('focus', onFocus)
    el.removeEventListener('blur', onBlur)
  }
}
